import { mdiChevronLeft, mdiCloseCircleOutline, mdiViewGrid } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import { PPDrawer } from 'components/PPDrawer'
import useConditionalEffect from 'hooks/useConditionalEffect'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { getFacebookPostTemplates, setDefaultFBBackgroundTemplateId, setFacebookTextBackground } from 'services/compose'
import {
  composerFacebookPostStatusSelector,
  composerImageUrlsSelector,
  composerLinkSelector,
  composerPostSelector,
  defaultFacebookBackgroundTemplateIdSelector,
  facebookBackgroundTemplatesSelector
} from 'services/compose/selectors'
import { checkFeatureAvailability } from 'services/product'
import { BackgroundTemplate, BRAND_FACEBOOK, ComposerFacebookPost, FEATURE_POST_FB_TEXT_ON_IMAGE } from 'shared'
import StoreState, { StoreThunkDispatch } from 'store/state'
import styles from './TextBackgroundSelector.pcss'

const css = require('styles/variables')

const VISIBLE_LIST_TILES = 10
const EMPTY_TEMPLATE_ID = 'pp_empty_template'
const TEMPLATE_EMPTY: BackgroundTemplate = {
  id: EMPTY_TEMPLATE_ID,
  code: 'None',
  thumbUrl: '',
  backgroundUrl: '',
  color: '#FFFFFF'
}

export function TextBackgroundSelector({ className }: { className?: string }) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const link = useSelector(composerLinkSelector)
  const images = useSelector(composerImageUrlsSelector)
  const isEmpty = useSelector(composerFacebookPostStatusSelector).isEmpty
  const fbPost = useSelector((state: StoreState) => composerPostSelector(state, BRAND_FACEBOOK)) as ComposerFacebookPost
  const defaultTemplateId = useSelector(defaultFacebookBackgroundTemplateIdSelector)
  const selectedTemplate = fbPost.textBackground
  const templates = useSelector(facebookBackgroundTemplatesSelector)
  const [popupOpen, setPopupOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const isEmptyRef = useRef(isEmpty)
  useEffect(() => {
    isEmptyRef.current = isEmpty
  }, [isEmpty])

  const defaultTemplateCheckboxLabel = useMemo(() => {
    const isCurrentSelectedDefault = (defaultTemplateId && selectedTemplate?.id === defaultTemplateId)
      || (!defaultTemplateId && !selectedTemplate)
    return `Save${isCurrentSelectedDefault ? 'd' : ''} as default (${selectedTemplate ? `${selectedTemplate.code}` : 'None'})`
  }, [selectedTemplate, defaultTemplateId])

  const openPopup = () => {
    setPopupOpen(true)
  }

  const closePopup = () => {
    setPopupOpen(false)
  }

  useConditionalEffect(([_defaultTemplateId]) => {
    // Initialize default template if applicable
    dispatch(getFacebookPostTemplates())
      .unwrap()
      .then((response) => {
        if (_defaultTemplateId && isEmptyRef.current) {
          dispatch(setFacebookTextBackground(response.find((t: any) => t.id === _defaultTemplateId)))
        }
      })
  }, [dispatch], [defaultTemplateId])

  // NOTE: Clear text background when link or images are added
  useEffect(() => {
    if (link || images.length > 0) {
      dispatch(setFacebookTextBackground(undefined))
    }
  }, [link, images.length, dispatch])

  const onItemClick = (template: BackgroundTemplate) => {
    const featureAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_FB_TEXT_ON_IMAGE))
    if (!featureAllowed) {
      return
    }
    if (template.id === selectedTemplate?.id) {
      dispatch(setFacebookTextBackground(undefined))
    } else {
      dispatch(setFacebookTextBackground(template))
    }
  }

  const onPopupItemClick = (template: BackgroundTemplate) => {
    if (template.id === EMPTY_TEMPLATE_ID) {
      dispatch(setFacebookTextBackground(undefined))
      return
    }
    const featureAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_FB_TEXT_ON_IMAGE))
    if (!featureAllowed) {
      return
    }
    if (template.id === selectedTemplate?.id) {
      dispatch(setFacebookTextBackground(undefined))
    } else {
      dispatch(setFacebookTextBackground(template))
    }
  }

  const toggle = () => {
    setExpanded((current) => !current)
  }

  const toggleSavedAsDefault = () => {
    const nextValue = selectedTemplate?.id === defaultTemplateId || selectedTemplate?.id === EMPTY_TEMPLATE_ID
      ? undefined
      : selectedTemplate?.id
    dispatch(setDefaultFBBackgroundTemplateId(nextValue))
  }

  return (
    <div className={className}>
      <Collapse in={expanded} timeout="auto" orientation="horizontal" collapsedSize={40}>
        <div className={styles.list}>
          <div className={styles['btn-toggle']} onClick={toggle}>
            {expanded && (
              <div className={styles['btn-collapse']}>
                <Icon path={mdiChevronLeft} size="28px" className={styles['icon-left']} />
              </div>
            )}
          </div>
          {templates.slice(0, VISIBLE_LIST_TILES).map((template) => {
            return (
              <BackgroundTile
                key={template.id}
                template={template}
                selected={selectedTemplate?.id === template.id}
                onClick={onItemClick}
              />
            )
          })}
          <div className={`${styles.tile} ${styles.btn}`} onClick={openPopup}>
            <Icon path={mdiViewGrid} size="20px" />
          </div>
        </div>
      </Collapse>

      <PPDrawer
        open={popupOpen}
        onClose={closePopup}
        title="Choose Background"
        size="md"
        footer={(
          <Button variant="contained" color="primary" onClick={closePopup}>
            <FormattedMessage id="actions.done" />
          </Button>
        )}
      >
        <FormControlLabel
          label={defaultTemplateCheckboxLabel}
          classes={{ label: styles.label, root: styles.checkboxContainer }}
          control={(
            <Checkbox
              checked={selectedTemplate?.id === defaultTemplateId}
              color="primary"
              size="small"
              onChange={toggleSavedAsDefault}
            />
          )}
        />
        <div className={styles.grid}>
          {[TEMPLATE_EMPTY, ...templates].map((template) => {
            return (
              <BackgroundTile
                key={template.id}
                big
                template={template}
                selected={(selectedTemplate?.id === template.id) || (template.id === EMPTY_TEMPLATE_ID && !selectedTemplate)}
                onClick={onPopupItemClick}
              />
            )
          })}
        </div>
      </PPDrawer>
    </div>
  )
}

interface BackgroundTileProps {
  template: BackgroundTemplate
  selected: boolean
  big?: boolean
  onClick: (template: BackgroundTemplate) => void
}
function BackgroundTile({ template, selected, big, onClick }: BackgroundTileProps) {
  const style = template.id === EMPTY_TEMPLATE_ID ? undefined : {
    backgroundImage: `url(${template.thumbUrl})`
  }

  const handleClick = () => {
    onClick(template)
  }

  return (
    <Tooltip title={template.code}>
      <div
        className={`${styles.tile} ${selected ? styles.selected : ''} ${big ? styles.big : ''}`}
        style={style}
        data-testid="background-tile"
        onClick={handleClick}
      >
        {template.id === EMPTY_TEMPLATE_ID && (
          <Icon path={mdiCloseCircleOutline} size="48px" color={css['--alert-red']} />
        )}
      </div>
    </Tooltip>
  )
}
